import { useSelector, useDispatch } from 'react-redux';
import { CloseOutlined, MailOutlined } from '@ant-design/icons';
import { Flex, Form, Typography, Input, Button, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setSelectedStep, setWorkflow } from '../../../redux/slices/connectorSlice';
import UserRoleDropdown from '../../UserRoleDropdown';
import { trimStringValues } from '../../../utils/trimStrings';

const { TextArea } = Input;

const NotificationInfo = () => {
    const [notificationDetails, setNotificationDetails] = useState({})
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const formValues = Form.useWatch([], form);

    const { selectedStep, workflow } = useSelector(state => state.connector);

    useEffect(() => {
        if (selectedStep) {
            setNotificationDetails({ ...selectedStep })
        }
    }, [selectedStep])

    useEffect(() => {
        let containerEle = document.getElementById("notification-container");
        const isNotificationNode = selectedStep.id && (
            selectedStep.data?.type === "notification" || 
            selectedStep.type === "notification"
        );
        
        
        if (isNotificationNode) {
            if (containerEle) {
                containerEle.style.display = "block";
            }
            // Set default values for notification
            const nodeData = selectedStep.data || selectedStep;
            const defaultValues = {
                name: nodeData?.name || "Notification",
                subject: nodeData?.subject || "",
                message: nodeData?.message || "",
                notifyTo: nodeData?.notifyTo || { user: [], role: [], group: [], userPicker: [] }
            };
            form.setFieldsValue(defaultValues);
            setNotificationDetails({ ...nodeData, ...defaultValues });
        } else {
            if (containerEle) {
                containerEle.style.display = "none";
            }
        }
    }, [selectedStep, form])

    const saveNotificationDetails = () => {
        const updatedNodes = workflow.nodes.map(node => {
            if (node.id === selectedStep.id) {
                return { 
                    ...node, 
                    data: {
                        ...node.data,
                        ...notificationDetails,
                        ...trimStringValues(formValues),
                        type: "notification",
                        name: formValues.name?.trim() || "Notification"
                    }
                };
            } else {
                return { ...node };
            }
        });
        dispatch(setWorkflow({ ...workflow, nodes: updatedNodes }));
        dispatch(setSelectedStep({}));
    }

    const closeNotificationDetails = () => {
        dispatch(setSelectedStep({}))
    }

    const onNotifySelect = (values) => {
        setNotificationDetails({ 
            ...notificationDetails, 
            notifyTo: { 
                user: values.user, 
                role: values.role, 
                group: values.group, 
                userPicker: values.userPicker 
            } 
        })
    }

    return (
        <div className='step-details-container' id="notification-container">
            <Flex className='step-details-header' align="center" justify="space-between">
                <Typography.Title level={5} className='margin-0'>
                    <Space>
                        <MailOutlined />
                        {t('Notification Details')}
                    </Space>
                </Typography.Title>
                <CloseOutlined className="icon-medium" onClick={closeNotificationDetails} />
            </Flex>
            <Flex vertical justify='space-between'>
                <div className='step-details-body'>
                    <Form form={form} onFinish={saveNotificationDetails} labelAlign="left" layout="vertical" style={{ height: "100%" }}>
                        <Flex vertical justify="space-between" style={{ height: "100%" }}>
                            <div>
                                <Form.Item
                                    label={t('Notification Name')}
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message: 'Notification name is required'
                                        }
                                    ]}
                                >
                                    <Input placeholder={t('Enter notification name')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('Email Subject')}
                                    name="subject"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message: 'Email subject is required'
                                        }
                                    ]}
                                >
                                    <Input placeholder={t('Enter email subject')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('Message')}
                                    name="message"
                                    rules={[
                                        {
                                            required: true,
                                            whitespace: true,
                                            message: 'Message is required'
                                        }
                                    ]}
                                >
                                    <TextArea 
                                        rows={4} 
                                        placeholder={t('Enter notification message')}
                                        showCount
                                        maxLength={500}
                                    />
                                </Form.Item>

                                <div>
                                    <div style={{ paddingBottom: "8px", marginTop: "16px" }}>
                                        {t('Send Notification To')}
                                    </div>
                                    <UserRoleDropdown 
                                        selectedOptions={notificationDetails.notifyTo ? {
                                            "user": notificationDetails?.notifyTo?.user || [],
                                            "role": notificationDetails?.notifyTo?.role || [],
                                            "group": notificationDetails?.notifyTo?.group || [],
                                            "userPicker": notificationDetails?.notifyTo?.userPicker || []
                                        } : { user: [], role: [], group: [], userPicker: [] }}
                                        onSelect={onNotifySelect}
                                        tabsRequired={["user", "group", "userPicker"]}
                                        getSpaceUsers={true} 
                                    />
                                </div>
                            </div>

                            <Form.Item className='step-details-action'>
                                <Flex>
                                    <Button 
                                        htmlType="submit" 
                                        style={{ width: "9.75rem", marginRight: "16px" }} 
                                        type="primary" 
                                        className="submit-button"
                                    >
                                        {t("Apply")}
                                    </Button>
                                    <Button 
                                        style={{ width: "9.75rem" }} 
                                        onClick={closeNotificationDetails}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                </Flex>
                            </Form.Item>
                        </Flex>
                    </Form>
                </div>
            </Flex>
        </div>
    )
}

export default NotificationInfo
